/* =========================================================================
   The "Mis recursos" catalog — the tools and files a codenec-user-client can
   download or open from /resources.

   Every client with role "codenec-user-client" sees this same list; there is no
   per-client entitlement. Admins and testers see it too, so the page can be
   checked without switching accounts (see canAccessResources in utils/roles.js).

   This is BUILD-TIME config, like config/courses.js and config/enrollment.js:
   adding a resource means editing this file and redeploying. That is the trade
   for having no backend, no Firestore read and no security rules to get wrong.
   If the list starts changing weekly, move it to Firestore — the page reads an
   array and does not care where the array came from.

   ---------------------------------------------------------------- SHAPE ----

   {
     id:      "unique-slug",     // stable; used as the React key
     kind:    "file" | "link",   // file = we serve it, link = it lives elsewhere
     href:    "...",             // see below
     titleKey / descriptionKey   // i18n keys, so the card is bilingual
     filename:                   // "file" only — the name the browser saves as
     size:                       // optional, shown on the card ("2.4 MB")
   }

   kind: "file"  → href is a path under codenec/public/, e.g.
                   "/resources/plantilla-cliente.zip". Drop the file in
                   codenec/public/resources/ and it ships with the build.
                   NOTE: files in public/ are served by Firebase Hosting with no
                   auth — anyone with the URL can fetch one. The menu item is
                   gated, the file itself is not. Do not put anything here that
                   would hurt if it leaked; if you need real protection, that is
                   the point to move to Firebase Storage with rules.

   kind: "link"  → href is an absolute URL (Drive, Notion, GitHub, a vendor's
                   site). Opens in a new tab instead of downloading.

   kind: "storage" → the file lives in Cloud Storage, not in the build, and is
                   gated by storage.rules. Give it `path` (the object path in
                   the bucket) instead of `href`; the page asks Firebase for a
                   download link when the person clicks, which only succeeds for
                   a signed-in client. Use this for anything too big to commit -
                   git refuses a file over 100 MB - or anything that should not
                   sit on a public URL. Publish a new build by uploading over
                   the SAME path, so this file never has to change.

   Titles and descriptions are i18n keys, NOT literal strings, because the app is
   Spanish-first — a hardcoded English title would show up untranslated for the
   audience this is actually for. Add the matching keys under "resources.items"
   in translations/en/common.json AND translations/es/common.json.

   ------------------------------------------------------------- EXAMPLES ----

   export const RESOURCES = [
     {
       id: "starter-kit",
       kind: "file",
       href: "/resources/starter-kit.zip",
       filename: "starter-kit.zip",
       size: "2.4 MB",
       titleKey: "resources.items.starterKit.title",
       descriptionKey: "resources.items.starterKit.description",
     },
     {
       id: "claude-code",
       kind: "link",
       href: "https://claude.com/claude-code",
       titleKey: "resources.items.claudeCode.title",
       descriptionKey: "resources.items.claudeCode.description",
     },
   ];

   ========================================================================= */

export const RESOURCES = [
    {
        // The desktop app. `path`, not `href`: the installer is ~279 MB, which
        // is far past what git accepts, and it is what a client pays for - so
        // it lives in Cloud Storage behind storage.rules instead of in
        // public/, where anyone with the URL could fetch it.
        //
        // The path has no version in it on purpose. A release is published by
        // uploading over this exact object, which keeps the card, the rules and
        // the update feed pointing at one place. The version a client is
        // offered is the one in desktop_release/current, read by the app
        // itself - see the backend's /codenec/desktop/version.
        id: "codenec-desktop",
        kind: "storage",
        path: "desktop/windows/Codenec-Setup.exe",
        filename: "Codenec-Setup.exe",
        size: "~279 MB",
        titleKey: "resources.items.codenecDesktop.title",
        descriptionKey: "resources.items.codenecDesktop.description",
    },
];

export default RESOURCES;
